import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Minus, Plus } from "lucide-react";

// Types
export interface Guest {
  id: number;
  age: number;
}

interface GuestManagerProps {
  totalGuests: number;
  guests: Guest[];
  onAddGuest: () => void;
  onRemoveGuest: () => void;
  onUpdateGuestAge: (guestId: number, newAge: number) => void;
  disabled?: boolean;
}

const CounterButton = ({
  onClick,
  children,
  disabled = false
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="w-12 h-12 rounded-full bg-gray-50 border-gray-200"
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </Button>
);

export const GuestManager: React.FC<GuestManagerProps> = ({
  totalGuests,
  guests,
  onAddGuest,
  onRemoveGuest,
  onUpdateGuestAge,
  disabled = false
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <span>Total number of guests</span>
        <div className="flex items-center space-x-4">
          {!disabled && (
            <CounterButton onClick={onRemoveGuest} disabled={disabled}>
              <Minus className="w-5 h-5" />
            </CounterButton>
          )}
          <span className="text-lg font-semibold w-8 text-center">
            {totalGuests}
          </span>
          {!disabled && (
            <CounterButton onClick={onAddGuest}>
              <Plus className="w-5 h-5" />
            </CounterButton>
          )}
        </div>
      </div>

      {guests.length > 0 && (
        <div className="space-y-4">
          {guests.map((guest, index) => (
            <div key={guest.id} className="py-3">
              <div className="flex items-center w-[100%]">
                <div className="flex items-center w-[100%] justify-between space-x-4">
                  <span className="text-sm font-medium">
                    Guest #{index + 1}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Age:</span>
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      value={guest.age}
                      onChange={(e) => {
                        const newAge = parseInt(e.target.value) || 0;
                        onUpdateGuestAge(guest.id, Math.max(0, newAge));
                      }}
                      className="w-16 h-8 text-sm rounded-lg"
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
